import type { ReactNode } from 'react';
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { prefersReducedMotion } from '@/theme/reducedMotion';
import '@/styles/page-transition.css';

/**
 * Fades the page that just resolved.
 *
 * `Suspense` stays outside and is keyed by pathname, so this subtree remounts
 * on navigation. `appear` is what actually runs the 150ms enter: without it a
 * fresh `SwitchTransition` paints the new page immediately. `out-in` still
 * applies when this instance survives the change.
 */
export function PageTransition({ children }: { children: ReactNode }): JSX.Element {
  const location = useLocation();
  const nodeRef = useRef<HTMLDivElement>(null);
  const reduce = prefersReducedMotion();

  return (
    <SwitchTransition mode="out-in">
      <CSSTransition
        key={location.pathname}
        nodeRef={nodeRef}
        timeout={reduce ? 0 : 150}
        classNames="page-fade"
        appear={!reduce}
        unmountOnExit
      >
        <div ref={nodeRef}>{children}</div>
      </CSSTransition>
    </SwitchTransition>
  );
}
